import React, { memo, useMemo } from "react";
import { Typography, useTheme, Link } from "@mui/material";
import { Row, Col, Card } from "v2/components/base/Flex/ui";
import { CabalGroupData } from "../types";
import ShillRecordItem from "./ShillRecordItem";

interface CabalGroupBlockProps {
  groupData: CabalGroupData;
}

/**
 * Component to display a group of shill records for a specific Cabal
 */
const CabalGroupBlock: React.FC<CabalGroupBlockProps> = memo(({ groupData }) => {
  const theme = useTheme();
  const { groupName, size, inviteLink, records, totalShills, uniqueSenderCount } = groupData;

  // Calculate statistics
  const { displayRecords, additionalRecords } = useMemo(() => {
    // Limit the number of records displayed to improve performance
    const displayRecords =
      records && records.length > 0
        ? records.slice(0, Math.min(records.length, 50)) // Display max 50 records per group
        : [];

    // Count of additional records not shown
    const additionalRecords = records && records.length > 50 ? records.length - 50 : 0;

    return { displayRecords, additionalRecords };
  }, [records]);

  return (
    <Card.Section
      sx={{
        width: "100%",
        p: 2,
        borderRadius: "12px",
        background: theme.palette.surface.container,
        border: `1px solid ${theme.palette.border.light}`,
        transition: "transform 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        },
      }}>
      {/* Header with Cabal Name and Statistics */}
      <Row type="bc" sx={{ mb: 2, alignItems: "center" }} fullWidth>
        {/* Cabal Name - clickable if inviteLink exists */}
        {inviteLink ? (
          <Link
            href={inviteLink}
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              fontSize: "1.1rem",
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }}>
            {groupName}
          </Link>
        ) : (
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "1.1rem" }}>
            {groupName}
          </Typography>
        )}

        {/* Statistics */}
        <Row type="cc" gap="8px">
          {/* Unique Senders */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              background: theme.palette.surface.bg,
              padding: "4px 8px",
              borderRadius: "4px",
              fontWeight: 500,
            }}>
            Unique Shillers: {uniqueSenderCount}
          </Typography>

          {/* Size */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              background: theme.palette.surface.bg,
              padding: "4px 8px",
              borderRadius: "4px",
              fontWeight: 500,
            }}>
            Size: {size}
          </Typography>

          {/* Total Shills */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text["btn-dark"],
              background: theme.palette.brand.primary,
              padding: "4px 8px",
              borderRadius: "4px",
              fontWeight: 500,
            }}>
            Shills: {totalShills.toFixed(0)}
          </Typography>
        </Row>
      </Row>

      {/* Records List */}
      <Col type="ss" gap="8px" fullWidth>
        {displayRecords.length > 0 ? (
          <>
            {displayRecords.map((record, index) => (
              <ShillRecordItem key={`${record.messageId}-${index}`} record={record} />
            ))}
            {additionalRecords > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                  mt: 1,
                  fontStyle: "italic",
                }}>
                +{additionalRecords} more records
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 1 }}>
            No records available
          </Typography>
        )}
      </Col>
    </Card.Section>
  );
});

export default CabalGroupBlock;
